import { useState, useRef, useEffect, useCallback } from 'react';
import { useStore } from '../store';
import { Section } from '../types';
import { Sparkles, Edit2, Save, X, FileText, Image as ImageIcon, RefreshCw, Maximize2, Minimize2 } from 'lucide-react';

interface SectionContentProps {
  section: Section;
  onRegenerate?: (selectedText: string, startIndex: number, endIndex: number) => void;
}

/**
 * SectionContent - Renders section content directly on the page
 * Shows a loading overlay during generation, then reveals content smoothly
 * Supports text selection with popup for rewrite/expand/shorten
 */
export default function SectionContent({ section, onRegenerate }: SectionContentProps) {
  const { updateSection } = useStore();
  const [isEditing, setIsEditing] = useState(false);
  const [showContent, setShowContent] = useState(!section.isGenerating && !section.isGeneratingImage);
  const [selectedText, setSelectedText] = useState('');
  const [selectionRange, setSelectionRange] = useState<{ start: number; end: number } | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Track when generation completes to trigger reveal animation
  useEffect(() => {
    if (!section.isGenerating && !section.isGeneratingImage && section.content) {
      const timer = setTimeout(() => setShowContent(true), 100);
      return () => clearTimeout(timer);
    } else if (section.isGenerating || section.isGeneratingImage) {
      setShowContent(false);
    }
  }, [section.isGenerating, section.isGeneratingImage, section.content]);

  // Close edit mode on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isEditing) setIsEditing(false);
        if (showPopup) setShowPopup(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, showPopup]);

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.selectionStart = textareaRef.current.value.length;
    }
  }, [isEditing]);

  // Handle text selection in edit mode
  const handleTextSelection = useCallback(() => {
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart;
      const end = textareaRef.current.selectionEnd;
      if (start !== end) {
        const selected = section.content.substring(start, end);
        setSelectedText(selected);
        setSelectionRange({ start, end });

        // Position popup near selection
        const rect = textareaRef.current.getBoundingClientRect();
        setPopupPosition({
          x: rect.left + rect.width / 2,
          y: rect.top - 10,
        });
        setShowPopup(true);
      } else {
        setSelectedText('');
        setSelectionRange(null);
        setShowPopup(false);
      }
    }
  }, [section.content]);

  // Handle text selection in view mode
  const handleViewTextSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim()) {
      const selected = selection.toString();
      setSelectedText(selected);

      // Find position in content
      const start = section.content.indexOf(selected);
      if (start !== -1) {
        setSelectionRange({ start, end: start + selected.length });

        // Position popup near selection
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        setPopupPosition({
          x: rect.left + rect.width / 2,
          y: rect.top - 10,
        });
        setShowPopup(true);
      }
    } else {
      setShowPopup(false);
    }
  }, [section.content]);

  // Handle regenerate action
  const handleRegenerate = (action: 'rewrite' | 'expand' | 'shorten') => {
    if (selectedText && selectionRange && onRegenerate) {
      let instructions = '';
      switch (action) {
        case 'rewrite':
          instructions = 'Rewrite this text with different wording while keeping the same meaning';
          break;
        case 'expand':
          instructions = 'Expand on this text with more detail and explanation';
          break;
        case 'shorten':
          instructions = 'Make this text more concise while keeping the key points';
          break;
      }
      onRegenerate(selectedText + '\n\nInstructions: ' + instructions, selectionRange.start, selectionRange.end);
      setShowPopup(false);
    }
  };

  // Show loading state while generating
  if (section.isGenerating || section.isGeneratingImage) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <div className="relative mb-6">
          <div className="absolute inset-0 w-20 h-20 border-4 border-accent/20 rounded-full animate-ping" />
          <div className="relative w-20 h-20 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            {section.isGeneratingImage ? (
              <ImageIcon size={24} className="text-accent" />
            ) : (
              <FileText size={24} className="text-accent" />
            )}
          </div>
        </div>
        <div className="text-center">
          <p className="text-lg font-medium text-gray-700 mb-1">
            {section.isGeneratingImage ? 'Creating Image...' : 'Writing Content...'}
          </p>
          <p className="text-sm text-gray-400">
            {section.isGeneratingImage
              ? 'Generating your image with AI'
              : 'Crafting your content with AI'}
          </p>
        </div>
        <div className="flex gap-1.5 mt-4">
          <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    );
  }

  // Selection popup component
  const SelectionPopup = () => {
    if (!showPopup || !selectedText) return null;

    return (
      <div
        className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-1 flex gap-1"
        style={{
          left: popupPosition.x,
          top: popupPosition.y,
          transform: 'translate(-50%, -100%)',
        }}
      >
        <button
          onClick={() => handleRegenerate('rewrite')}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded"
        >
          <RefreshCw size={12} />
          Rewrite
        </button>
        <button
          onClick={() => handleRegenerate('expand')}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded"
        >
          <Maximize2 size={12} />
          Expand
        </button>
        <button
          onClick={() => handleRegenerate('shorten')}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded"
        >
          <Minimize2 size={12} />
          Shorten
        </button>
      </div>
    );
  };

  // Show content
  if (section.content) {
    if (isEditing) {
      return (
        <div className="relative">
          <SelectionPopup />
          <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-200">
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Edit2 size={12} />
              Editing - Select text for options (Esc to cancel)
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setIsEditing(false)}
                className="text-xs flex items-center gap-1 px-2 py-1 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
              >
                <X size={12} />
                Cancel
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="text-xs flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
              >
                <Save size={12} />
                Done
              </button>
            </div>
          </div>
          <textarea
            ref={textareaRef}
            value={section.content}
            onChange={(e) => updateSection(section.id, { content: e.target.value })}
            onMouseUp={handleTextSelection}
            onKeyUp={handleTextSelection}
            className="w-full min-h-[200px] p-3 bg-gray-50 border border-gray-300 rounded-lg text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-accent focus:bg-white resize-y"
          />
        </div>
      );
    }

    // View mode - content with reveal animation
    return (
      <div
        className={`transition-all duration-500 ease-out ${
          showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <SelectionPopup />
        <div
          ref={contentRef}
          className="whitespace-pre-wrap text-gray-800 leading-relaxed"
          onMouseUp={handleViewTextSelection}
        >
          {section.content}
        </div>
        <button
          onClick={() => setIsEditing(true)}
          className="mt-3 inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-accent hover:bg-accent/10 rounded opacity-60 hover:opacity-100 transition-all"
        >
          <Edit2 size={10} />
          Edit
        </button>
      </div>
    );
  }

  // Empty state
  return (
    <div className="flex flex-col items-center justify-center py-12 text-gray-400">
      <Sparkles size={32} className="mb-3 opacity-50" />
      <p className="text-sm">Click <span className="font-medium text-accent">Generate</span> to create content</p>
    </div>
  );
}
